import { defineComponent as _defineComponent } from 'vue'
import { vShow as _vShow, createElementVNode as _createElementVNode, withDirectives as _withDirectives, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "condition-chart-wrapper" }
const _hoisted_2 = { class: "title" }
const _hoisted_3 = { class: "note" }
const _hoisted_4 = { class: "avg" }
const _hoisted_5 = { class: "condition-legend" }
const _hoisted_6 = { class: "legend-label" }
const _hoisted_7 = { class: "legend-value" }

import type { EChartsOption } from "echarts";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { computed, ref } from "vue";
import Chart from "@/views/base/Chart/Chart.vue";

type RawItem = {
  dates: string;
  chartData:
    | { value: number; condition: string; color?: string; size?: number }
    | { value: number; condition: string; color?: string; size?: number }[]
    | null;
};

type RangeKey = "1w" | "1m" | "3m" | "6m" | "1y";

type SafeOpts = {
  baseline?: dayjs.Dayjs | string | Date | null; // 年なし(M/D)の年越し補正で参照する基準日
  preferEnd?: boolean; // "A〜B" 入力のとき末尾(B)を採るなら true（既定は先頭）
};

// --- Props --------------------------------------------------

export default /*@__PURE__*/_defineComponent({
  __name: 'ConditionLineChart',
  props: {
    bodyConditions: {},
    icons: { default: () => ({
      veryGood: "/server/necs/images/body_condition_excellent_active.svg",
      good: "/server/necs/images/body_condition_good_active.svg",
      normal: "/server/necs/images/body_condition_normal_active.svg",
      bad: "/server/necs/images/body_condition_poor_active.svg",
      veryBad: "/server/necs/images/body_condition_terrible_active.svg",
    }) },
    range: {},
    aggregationMap: { default: () => ({
      "1w": 1,
      "1m": 5,
      "3m": 15,
      "6m": 30,
      "1y": 90,
    }) },
    pointSize: { default: 10 },
    colors: { default: () => ({
      1: "#E16849",
      2: "#E99C8D",
      3: "#F1C151",
      4: "#94DB28",
      5: "#33B197",
    }) },
    labels: { default: () => ({
      1: "とてもわるい",
      2: "わるい",
      3: "ふつう",
      4: "いい",
      5: "とてもいい",
    }) },
    startDate: { default: dayjs().subtract(6, "day").format("YYYY-MM-DD") },
    endDate: { default: dayjs().format("YYYY-MM-DD") },
    showAggLabel: { type: Boolean, default: true },
    titleCond: { default: () => ({
      show: true,
      pos: {
        left: "10px",
        top: "10px",
      },
      textStyle: {
        fontFamily: "Zen Maru Gothic, sans-serif",
        fontSize: "18px",
        fontWeight: "normal",
      },
    }) },
    titleAvg: { default: () => ({
      show: true,
      pos: {
        right: "60px",
        top: "10px",
      },
      textStyle: {
        fontFamily: "Zen Maru Gothic, sans-serif",
        fontSize: "16px",
        fontWeight: "normal",
      },
    }) },
    grid: { default: () => ({
      left: 0,
      right: 0,
      top: "5%",
      bottom: "5%",
    }) }
  },
  setup(__props: any) {

dayjs.extend(customParseFormat);

// -- Type ---------------------------------------------------------

// 生データ型
const props = __props;

// --- Constant --------------------------------------------------
const noteOffset = ref(0);

// 標準レンジの日数マップ
const standardDaysMap: Record<RangeKey, number> = {
  "1w": 7,
  "1m": 30, // 1カ月を30日で扱う
  "3m": 90,
  "6m": 180,
  "1y": 365,
};

// --- Functions ------------------------------------------------

// 終了日をラベルにする
// baselineを元にM/D形式で日付を生成（年またぎに対応）
function fmtMD(
  dateStr: string | undefined,
  baseline?: dayjs.Dayjs | string | null
) {
  if (!dateStr) return "";
  const endStr = String(dateStr).split(/[〜~]/).pop()?.trim() ?? "";
  const d = toDayjsSafe(endStr, baseline ? { baseline } : undefined);
  return d ? d.format("M/D") : endStr || "";
}

// データポイントを抽出して正規化する
function extractPoint(item: RawItem | undefined) {
  if (!item || !item.chartData) {
    return null;
  }

  const list = Array.isArray(item.chartData)
    ? item.chartData
    : [item.chartData];
  const values = list.map((cd) => cd.value).filter((v) => v != null);

  if (values.length === 0) {
    return null;
  }

  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  const rounded = Math.round(avg);

  return {
    value: avg,
    condition: props.labels![rounded],
    color: props.colors![rounded],
    size: list[0].size ?? 15,
  };
}

// 平均値を1〜5に丸める
function normalizeValue(val: number | null): number | null {
  if (val === null) return null;
  const rounded = Math.round(val);
  return Math.min(5, Math.max(1, rounded));
}

// dayjsに変換（最新拡張版：レンジ対応 + 年越し補正 + M/D/JP表記対応）
function toDayjsSafe(
  s?: string | number | dayjs.Dayjs,
  fallbackYearOrOpts?: number | SafeOpts
): dayjs.Dayjs | null {
  if (
    s === null ||
    s === undefined ||
    (typeof s === "string" && s.trim() === "")
  ) {
    return null;
  }

  // オプション解釈（後方互換：numberはfallbackYearとして扱う）
  let fallbackYear: number | undefined;
  let opts: SafeOpts | undefined;
  if (typeof fallbackYearOrOpts === "number") {
    fallbackYear = fallbackYearOrOpts;
  } else if (typeof fallbackYearOrOpts === "object" && fallbackYearOrOpts) {
    opts = fallbackYearOrOpts;
  }

  // baseline の Dayjs 化
  let baseD: dayjs.Dayjs | null = null;
  if (opts?.baseline !== undefined && opts?.baseline !== null) {
    if (dayjs.isDayjs(opts.baseline)) {
      baseD = opts.baseline.isValid() ? opts.baseline : null;
    } else if (typeof opts.baseline === "string") {
      const bStr = opts.baseline.trim();
      const bStrict = dayjs(
        bStr,
        ["YYYY-MM-DD", "YYYY/MM/DD", "YYYY.MM.DD", "YYYY年M月D日"],
        true
      );
      baseD = bStrict.isValid()
        ? bStrict
        : dayjs(bStr).isValid()
          ? dayjs(bStr)
          : null;
    } else if (opts.baseline instanceof Date) {
      const bd = dayjs(opts.baseline);
      baseD = bd.isValid() ? bd : null;
    }
  }

  // 既に Dayjs の場合
  if (dayjs.isDayjs(s)) {
    return s.isValid() ? s : null;
  }

  // 数値（Unix 時間など）
  if (typeof s === "number") {
    const d = dayjs(s);
    return d.isValid() ? d : null;
  }

  // 文字列
  const raw = String(s).trim();
  if (!raw) return null;

  // "A〜B" / "A~B" → 先頭 or 末尾を選択
  const parts = raw
    .split(/[〜~]/)
    .map((t) => t.trim())
    .filter(Boolean);
  const str =
    parts.length > 1
      ? opts?.preferEnd
        ? parts[parts.length - 1]
        : parts[0]
      : raw;

  // --- M/D（年なし）/ M月D日 / 区切りバリエーション を優先処理 ---
  // 例: "4/10", "4-10", "4.10", "4 10", "4月10日"
  const md = str.match(/^\s*(\d{1,2})\s*[/.\-\s月]\s*(\d{1,2})\s*(?:日)?\s*$/);
  if (md) {
    const mm = Number(md[1]);
    const dd = Number(md[2]);
    if (!(mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31)) return null;

    // 年：fallbackYear > baseline.year() > 現在年
    let year =
      typeof fallbackYear === "number"
        ? fallbackYear
        : baseD
          ? baseD.year()
          : dayjs().year();

    let candidate = dayjs(`${year}-${mm}-${dd}`, "YYYY-M-D", true);
    if (!candidate.isValid()) return null;

    // baselineがある場合の年越し補正（対称）
    if (baseD) {
      const bMonth = baseD.month(); // 0..11
      if (bMonth === 11 && mm === 1 && candidate.isBefore(baseD, "day")) {
        candidate = candidate.add(1, "year");
      } else if (bMonth === 0 && mm === 12 && candidate.isAfter(baseD, "day")) {
        candidate = candidate.subtract(1, "year");
      } else {
        const diffDays = candidate.diff(baseD, "day");
        if (Math.abs(diffDays) > 200) {
          candidate =
            diffDays > 0
              ? candidate.subtract(1, "year")
              : candidate.add(1, "year");
        }
      }
    }

    return candidate.isValid() ? candidate : null;
  }

  // 年ありの厳密パース
  const fmtsStrict = [
    "YYYY-MM-DD",
    "YYYY/MM/DD",
    "YYYY.MM.DD",
    "YYYY年M月D日",
    "YYYY-M-D",
    "YYYY/M/D",
    "YYYY.M.D",
  ];
  let d = dayjs(str, fmtsStrict, true);
  if (d.isValid()) return d;

  // 緩いパース
  d = dayjs(str);
  return d.isValid() ? d : null;
}

// 日付の範囲でデータを抽出
function filterByStartEnd(
  items: typeof props.bodyConditions,
  start?: string | null,
  end?: string | null
) {
  if (!start && !end) return items;

  const s = start ? toDayjsSafe(start) : null;
  const e = end
    ? toDayjsSafe(end, s ? { baseline: s, preferEnd: true } : undefined)
    : null;

  // 無効な日付は無視して元配列を返す
  if ((s && !s.isValid()) || (e && !e.isValid())) return items;

  return items.filter((it) => {
    // SafeOpts で baseline を渡す（型エラー回避）
    const d = toDayjsSafe(
      (it?.dates ?? "").split("〜")[0],
      s ? { baseline: s } : undefined
    );
    if (!d) return false;
    if (s && d.isBefore(s)) return false;
    if (e && d.isAfter(e)) return false;
    return true;
  });
}

// 日数差を計算
function daysBetweenDates(
  startStr: string | undefined,
  endStr: string | undefined
): number {
  if (!startStr || !endStr) return 0;

  const sStr = String(startStr).split(/[〜~]/)[0].trim();
  const endParts = String(endStr).split(/[〜~]/);
  const eStr = (endParts[endParts.length - 1] || "").trim();

  const s = toDayjsSafe(sStr);
  const e = toDayjsSafe(eStr, { baseline: s, preferEnd: true });

  if (!s || !e) return 0;

  const diff = e.diff(s, "day"); // 差分（日単位）
  return diff >= 0 ? diff + 1 : 0; // 同日なら1日としてカウント
}

// 集約幅（平均化日数）を決める
function decideChunkAndLabel(
  rangeKey: string | undefined,
  itemsLocal: typeof props.bodyConditions
) {
  let chunkSize = 1;
  let useCalendarMonth = false; // カレンダーを月単位で集約するかどうか

  if (!itemsLocal || itemsLocal.length === 0)
    return { chunkSize, useCalendarMonth };

  // カスタム範囲: 日付差で判定 -----------------------------------------------
  const firstDateStr = itemsLocal[0]?.dates ?? "";
  const lastDateStr = itemsLocal[itemsLocal.length - 1]?.dates ?? "";
  const totalDays =
    daysBetweenDates(firstDateStr, lastDateStr) || itemsLocal.length;

  const stdDays = rangeKey
    ? standardDaysMap[rangeKey as keyof typeof standardDaysMap]
    : undefined;
  const isStandardRange = !!(stdDays && totalDays === stdDays);

  //  レンジ指定 --------------------------------------------------------------
  if (isStandardRange) {
    // 平均化日数を取得
    const typeOfRangeKey = rangeKey as keyof typeof props.aggregationMap;
    const agg = props.aggregationMap![typeOfRangeKey];
    chunkSize = Math.max(1, agg); // 該当項目がない場合は1
    // 6カ月・1年の場合は月平均にする
    if (rangeKey === "6m" || rangeKey === "1y") {
      useCalendarMonth = true;
    }
    return { chunkSize, useCalendarMonth };
  }

  // 31日以下は常に日次点（chunkSize=1）
  if (totalDays <= 31) {
    chunkSize = 1; // ～31日までは日次
    useCalendarMonth = false;
  } else if (totalDays <= 90) {
    chunkSize = 15; // ～90日は15日平均
    useCalendarMonth = false;
  } else if (totalDays <= 180) {
    useCalendarMonth = true; // ～180日は月平均（30日相当）
    chunkSize = 30; // ダミー値
  } else {
    useCalendarMonth = true; // 180日超も月平均
    chunkSize = 30; // ダミー値
  }
  return { chunkSize, useCalendarMonth };
}

// 集計処理
function aggregate(
  items: RawItem[],
  aggregationDays: number,
  colors: Record<number, string> = props.colors!,
  labels: Record<number, string> = props.labels!
) {
  if (!items || items.length === 0) return [];

  if (aggregationDays <= 1) {
    // 日次そのまま
    return items.map((it) => {
      const p = extractPoint(it);
      const val = p ? p.value : null;
      const rounded = val !== null ? Math.round(val) : null;

      return {
        x: it.dates,
        y: val,
        start: it.dates,
        end: it.dates,
        color: rounded !== null ? colors[rounded] : undefined,
        size: p?.size,
        condition: rounded !== null ? labels[rounded] : null,
      };
    });
  }

  const res: {
    x: string;
    y: number | null;
    start?: string;
    end?: string;
    color?: string;
    size?: number;
    condition?: string | null;
  }[] = [];

  for (let i = 0; i < items.length; i += aggregationDays) {
    const slice = items.slice(i, i + aggregationDays);
    const values = slice
      .map((s) => extractPoint(s)?.value)
      .filter((v): v is number => v !== null && v !== undefined);

    // 平均値
    const avg = Math.floor(values.reduce((a, b) => a + b, 0) / values.length);

    const rounded = avg !== null ? Math.round(avg) : null;

    const start = slice[0]?.dates ?? "";
    const end = slice[slice.length - 1]?.dates ?? start;

    const rep =
      slice.map((s) => extractPoint(s)).find((p) => p && p.value !== null) ??
      null;

    res.push({
      x: start === end ? start : `${start}〜${end}`,
      y: avg,
      start,
      end,
      color: rounded !== null ? colors[rounded] : undefined,
      size: rep?.size,
      condition: rounded !== null ? labels[rounded] : null,
    });
  }

  return res;
}

// 月ごとにグループ化して平均を計算する
// （最後の月は途中日数でもそのまま平均）
function aggregateByCalendarMonth(
  itemsLocal: typeof props.bodyConditions,
  colors: Record<number, string> = props.colors!,
  labels: Record<number, string> = props.labels!
) {
  if (!itemsLocal || itemsLocal.length === 0) return [];

  // baseline は先頭の有効日を使う（年なし M/D の補正に使用）
  const firstRaw = itemsLocal[0]?.dates ?? "";
  const firstStartStr = String(firstRaw).split(/[〜~]/)[0].trim();
  const baseline = toDayjsSafe(firstStartStr) ?? dayjs();

  // items の日付をパースして monthKey (YYYY-MM) ごとにグループ化
  const groups: Record<
    string,
    {
      items: typeof itemsLocal;
    }
  > = {};

  for (const it of itemsLocal) {
    const raw = it?.dates ?? "";
    // 先頭の日付文字列（例: "2025-01-01〜2025-01-07" → "2025-01-01"）
    const startStr = String(raw).split(/[〜~]/)[0].trim();
    const d = toDayjsSafe(startStr, { baseline });

    if (!d) {
      const key = "unknown";
      (groups[key] ??= { items: [] }).items.push(it);
      continue;
    }

    // YYYY-MM 形式でキー化
    const key = d.format("YYYY-MM");
    (groups[key] ??= { items: [] }).items.push(it);
  }

  // グループキーを日付順にソート
  const keys = Object.keys(groups).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));

  const res: {
    x: string;
    y: number | null;
    start?: string;
    end?: string;
    color?: string;
    size?: number;
    condition?: string | null;
  }[] = [];

  // グループごとに平均を計算
  for (const key of keys) {
    const slice = groups[key].items;
    if (!slice || slice.length === 0) continue;

    const values = slice
      .map((s) => {
        const p = Array.isArray(s.chartData) ? s.chartData[0] : s.chartData;
        return p ? p.value : null;
      })
      .filter((v): v is number => v !== null && v !== undefined);

    const avg = values.length
      ? values.reduce((a, b) => a + b, 0) / values.length
      : null;
    const norm = avg !== null ? normalizeValue(avg) : null;

    // 代表アイコンサイズ（あるものの最初）
    const rep =
      slice.map((s) => extractPoint(s)).find((p) => p && p.value !== null) ??
      null;

    // その月の「実データの最小日／最大日」を求める
    let minDay: dayjs.Dayjs | null = null;
    let maxDay: dayjs.Dayjs | null = null;
    for (const s of slice) {
      const raw = s?.dates ?? "";
      const startStr = String(raw).split(/[〜~]/)[0].trim();
      const endParts = String(raw).split(/[〜~]/);
      const endStr = (endParts[endParts.length - 1] || "").trim();

      const sd = toDayjsSafe(startStr, { baseline });
      const ed = toDayjsSafe(endStr, {
        baseline: sd ?? baseline,
        preferEnd: true,
      });

      if (sd && (!minDay || sd.isBefore(minDay))) minDay = sd;
      if (ed && (!maxDay || ed.isAfter(maxDay))) maxDay = ed ?? sd ?? maxDay;
    }

    const start = minDay ? minDay.format("YYYY-MM-DD") : slice[0]?.dates ?? "";
    const end = maxDay ? maxDay.format("YYYY-MM-DD") : start;

    // 月ラベル（例: "1月"）
    const monthLabel = (() => {
      const md = dayjs(key, "YYYY-MM", true);
      return md.isValid() ? md.format("M月") : "unknown";
    })();

    res.push({
      x: monthLabel, // 表示は月ラベル
      y: norm,
      start, // 実データ最小日
      end, // 実データ最大日
      color: norm !== null ? colors[norm] : undefined,
      size: rep?.size,
      condition: norm !== null ? labels[norm] : null,
    });
  }

  return res;
}

// 平均体調の説明文を作る
function makeExplainTitleFromAggregated(
  aggregatedArr: { y: number | null | undefined }[],
  labels: Record<number, string>
): string {
  const values = aggregatedArr
    .map((a) => a.y)
    .filter((v): v is number => typeof v === "number" && !Number.isNaN(v));

  if (values.length === 0) return "平均体調：データなし";

  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  const score = Math.max(1, Math.min(5, Math.round(avg)));

  return `平均体調：${labels[score]}`;
}

// 平均表示のメタ（期間ルールに準拠）
function getPeriodMeta(
  totalDaysRange: number,
  useCalendarMonth: boolean,
  dataaggregationDays: number
): { isAverage: boolean; unitLabel: "日" | "月" | null } {
  // 91日以上は月平均／181日以上は月平均＋3か月毎ラベル
  if (totalDaysRange >= 91) {
    return { isAverage: true, unitLabel: "月" };
  }
  // 32～90日は 15日平均
  if (totalDaysRange >= 32) {
    return { isAverage: true, unitLabel: "日" };
  }
  // 30・31日、8～29日は日次（平均ではない）
  return { isAverage: false, unitLabel: null };
}

// ラベル生成
function getAxisLabelsAndIndices(params: {
  range?: RangeKey;
  totalDaysRange: number;
  xLabels: string[]; // 軸カテゴリ（M/D や "M月" 等）
  aggregated: {
    x: string;
    y: number | null;
    start?: string;
    end?: string;
    color?: string;
    size?: number;
    condition?: string | null;
  }[];
  items: { dates: string }[]; // filterByStartEnd 済みの元データ
  useCalendarMonth: boolean; // 月次集約かどうか
  dataaggregationDays: number; // チャンク長（例：15, 30）
}) {
  const { range, totalDaysRange, useCalendarMonth, dataaggregationDays } =
    params;

  let labeledIndices: number[] = [];
  let labelsForXAxis: string[] = [];
  let xLabels = params.xLabels.slice(); // 必要なら上書き（1y→13本化）
  let adjustedXLabels: string[] = xLabels.slice();

  const firstLabel = xLabels[0];

  // 実データの先頭をbaselineに採用（年越し補正用）
  const baselineD = (() => {
    const firstAggStart = params.aggregated?.[0]?.start ?? null;
    const firstItemStart =
      (params.items?.[0]?.dates ?? "").split(/[〜~]/)[0]?.trim() || null;
    return (
      toDayjsSafe(firstAggStart ?? undefined) ??
      toDayjsSafe(firstItemStart ?? undefined) ??
      toDayjsSafe(firstLabel ?? undefined) ??
      null
    );
  })();

  const firstD = baselineD ?? toDayjsSafe(firstLabel ?? undefined);
  const isMonthStart = (d: dayjs.Dayjs) => d.isValid() && d.date() === 1;

  // 末尾月の完了可否（>90 以上の月次ラベルで使用）
  const getLastMonthInfo = () => {
    const lastAgg = params.aggregated[params.aggregated.length - 1];
    const lastRawItems = params.items[params.items.length - 1]?.dates ?? "";
    const [, lastRawItemsEnd] = lastRawItems.split("〜");
    const lastEndStr =
      lastAgg?.end ??
      (lastRawItemsEnd
        ? lastRawItemsEnd.trim()
        : lastRawItems.split("〜")[0]?.trim());

    // preferEnd + baseline で終了日を安全パース
    const lastEndD =
      toDayjsSafe(lastEndStr ?? undefined, {
        baseline: firstD ?? baselineD ?? undefined,
        preferEnd: true,
      }) ?? toDayjsSafe(lastEndStr ?? undefined, firstD?.year());

    const monthNum = lastEndD ? lastEndD.month() + 1 : null;
    const isComplete = lastEndD
      ? lastEndD.date() === lastEndD.daysInMonth()
      : false;
    return { monthNum, isComplete };
  };

  // === ラベル表示ルール ===
  if (totalDaysRange >= 181) {
    // 6～12か月：月平均／ラベルは「3か月毎」
    labelsForXAxis = xLabels.map((lbl, idx) => {
      const t = (lbl ?? "").trim();
      const mLabel = t.match(/^(\d{1,2})月$/);
      if (mLabel) {
        // ラベルは3か月毎
        return idx % 3 === 0 ? t : "";
      }
      const d = toDayjsSafe(t, { baseline: firstD ?? baselineD ?? undefined });
      return d && d.date() === 1 ? `${d.month() + 1}月` : "";
    });

    if (labelsForXAxis.length > 0 && !labelsForXAxis[0]) {
      labelsForXAxis[0] = xLabels[0] ?? "";
    }
  } else if (totalDaysRange >= 91) {
    // 3～6か月：月平均／ラベルは「毎月」
    const { monthNum: lastMonthNum, isComplete: isLastMonthComplete } =
      getLastMonthInfo();
    labelsForXAxis = xLabels.map((lbl) => {
      const t = (lbl ?? "").trim();
      const mLabel = t.match(/^(\d{1,2})月$/);
      if (mLabel) {
        return t;
      }
      const d = toDayjsSafe(t, { baseline: firstD ?? baselineD ?? undefined });
      if (!d) return "";
      if (isMonthStart(d)) {
        const m = d.month() + 1;
        const hideLast =
          lastMonthNum && m === lastMonthNum && !isLastMonthComplete;
        return hideLast ? "" : `${m}月`;
      }
      return "";
    });

    if (labelsForXAxis.length > 0 && !labelsForXAxis[0]) {
      labelsForXAxis[0] = xLabels[0] ?? "";
    }
  } else if (totalDaysRange >= 32) {
    const firstEnd = toDayjsSafe(xLabels[0]); // 最初の区間の終了日
    if (!firstEnd) {
      labelsForXAxis = xLabels.slice();
      labeledIndices = xLabels.map((_, i) => i);
      adjustedXLabels = xLabels;
    } else {
      labelsForXAxis = xLabels.map((lbl) => {
        const d = toDayjsSafe(lbl);
        if (!d) return "";

        const diff = d.diff(firstEnd, "day");
        return diff % 15 === 0 ? lbl : "";
      });

      labeledIndices = labelsForXAxis
        .map((lbl, i) => (lbl ? i : -1))
        .filter((i) => i >= 0);

      adjustedXLabels = xLabels;
    }
  } else if (totalDaysRange >= 30) {
    // 1か月（30・31日）／日次データ：ラベルは「5日毎」
    const startStr = firstLabel;
    const s = toDayjsSafe(xLabels[0]);
    const useStep = 7;

    labelsForXAxis = xLabels.map((lbl, idx) => {
      const d = toDayjsSafe(lbl);
      // const diffInclusive = s && d ? d.diff(s, "day") + 1 : NaN;
      const diffInclusive = s && d ? d.diff(s, "day") : NaN;
      // 先頭は必ず表示、以降は包括日数が5の倍数で表示
      return idx === 0 ||
        (Number.isFinite(diffInclusive) && diffInclusive % useStep === 0)
        ? lbl
        : "";
    });

    if (labelsForXAxis.length > 0 && !labelsForXAxis[0]) {
      labelsForXAxis[0] = xLabels[0] ?? "";
    }
  } else if (totalDaysRange >= 8) {
    // 1週間～1カ月未満（8～29日）／日次データ：ラベルは「5日毎」
    const startStr = firstLabel;
    const s = toDayjsSafe(xLabels[0]);
    const useStep = 7;

    labelsForXAxis = xLabels.map((lbl, idx) => {
      const d = toDayjsSafe(lbl ?? undefined, {
        baseline: firstD ?? baselineD ?? undefined,
      });
      const diffInclusive = s && d ? d.diff(s, "day") : NaN;
      // 先頭は必ず表示、以降は包括日数が5の倍数で表示
      return idx === 0 ||
        (Number.isFinite(diffInclusive) && diffInclusive % useStep === 0)
        ? lbl
        : "";
    });

    if (labelsForXAxis.length > 0 && !labelsForXAxis[0]) {
      labelsForXAxis[0] = xLabels[0] ?? "";
    }
  } else {
    // 1週間以内（1～7日）／日次データ：ラベルは「日毎」
    labelsForXAxis = xLabels.map((lbl) => lbl);
  }

  // --- labeledIndices（縦線位置） ---
  labeledIndices = (() => {
    if (!params.aggregated || params.aggregated.length === 0) return [];

    // 6カ月超（181日以上）は「軸は毎月」＝全インデックス
    if (totalDaysRange >= 181) {
      return Array.from({ length: xLabels.length }, (_, i) => i);
    }

    if (range === "1y" || range === "6m" || range === "1w") {
      // 1年・6か月・1週間は全点に縦線（従来仕様）
      return Array.from({ length: params.aggregated.length }, (_, i) => i);
    }
    // その他：ラベルがある場所に縦線
    const idx: number[] = [];
    labelsForXAxis.forEach((lbl, i) => {
      if (lbl) idx.push(i);
    });

    // 右端を必ず追加する（重複は避ける）
    const lastIndex = xLabels.length - 1;
    if (!idx.includes(lastIndex)) {
      idx.push(lastIndex);
    }
    return idx;
  })();

  return {
    adjustedXLabels, // 軸カテゴリ
    labelsForXAxis, // 軸に表示するラベル文字列
    labeledIndices, // 縦線インデックス
  };
}

function generateDateAxis(start: string, end: string): string[] {
  const s = dayjs(start);
  const e = dayjs(end);
  const arr: string[] = [];

  let cur = s.clone();
  while (cur.isBefore(e) || cur.isSame(e, "day")) {
    arr.push(cur.format("YYYY-MM-DD"));
    cur = cur.add(1, "day");
  }
  return arr;
}

// ===================================================================
//  Vue Life Cycle

// computed
const conditionLineOption = computed<EChartsOption>(() => {
  let aggregated: {
    x: string;
    y: number | null;
    start?: string;
    end?: string;
    color?: string;
    size?: number;
    condition?: string | null;
  }[] = [];
  const rawItems = props.bodyConditions || [];
  const range = props.range;

  const dateAxis =
    props.startDate && props.endDate
      ? generateDateAxis(props.startDate, props.endDate)
      : [];

  // まずデータのある日だけ抽出
  const filtered = filterByStartEnd(
    rawItems,
    props.startDate ?? null,
    props.endDate ?? null
  );

  // カレンダー全日付にマッピング（欠損日は chartData: null）
  const items = dateAxis.map((date) => {
    const found = filtered.find((it) => it.dates === date);
    return found ?? { dates: date, chartData: null };
  });

  // 集約幅を決定
  const { chunkSize: autoChunk, useCalendarMonth: useCalendarMonthFromDecide } =
    decideChunkAndLabel(range, items);
  let useCalendarMonth = useCalendarMonthFromDecide;

  // 集計 ---------------------------------------------------------------------
  if (range === "1m") {
    // 1カ月は平均化せず、日次データをそのまま出力する
    aggregated = aggregate(items, 1);
    useCalendarMonth = false;
  } else if (range === "6m") {
    // 6カ月レンジは月単位で集約する
    aggregated = aggregateByCalendarMonth(items);
    useCalendarMonth = true;
  } else {
    if (useCalendarMonthFromDecide) {
      // range: 1yの場合は月次集約を使う（点は月次のまま）
      aggregated = aggregateByCalendarMonth(items);
      useCalendarMonth = true;
    } else {
      aggregated = aggregate(items, autoChunk);
      useCalendarMonth = false;
    }
  }

  // baseline = 実データの先頭開始日（年越し補正の基準）
  const firstItemStart = (items[0]?.dates ?? "").split(/[〜~]/)[0].trim();
  const baselineD = toDayjsSafe(firstItemStart ?? undefined);

  // 期間の日数換算 ------------------------------------------------------------
  const totalDaysRange = (() => {
    if (range && standardDaysMap[range as keyof typeof standardDaysMap]) {
      return standardDaysMap[range as keyof typeof standardDaysMap];
    }
    if (props.startDate && props.endDate) {
      return dayjs(props.endDate).diff(dayjs(props.startDate), "day") + 1;
    }
    return items.length;
  })();

  // ラベル -------------------------------------------------------------------
  let xLabels = (() => {
    if (useCalendarMonth) {
      return aggregated.map((d) => d.x);
    }

    if (totalDaysRange >= 32 && totalDaysRange <= 90) {
      return aggregated.map((d) => {
        const raw = d.end ?? d.start ?? "";
        const dd = toDayjsSafe(raw);
        return dd ? dd.format("M/D") : raw;
      });
    }

    return dateAxis.map((d) => dayjs(d).format("M/D"));
  })();

  // 描画データ -----------------------------------------------------------------
  let seriesData = aggregated.map((d) => {
    // 各点の開始・終了（なければ x から補完）
    const startStr =
      d.start ??
      (typeof d.x === "string" && d.x.includes("〜")
        ? (d.x as string).split("〜")[0]
        : (d.x as string));
    const endStr =
      d.end ??
      (typeof d.x === "string" && d.x.includes("〜")
        ? (d.x as string).split("〜")[1]
        : (d.x as string));

    // 値に応じてカラーマップで点色を決める
    const decidePointColor = () => {
      if (d.y === null || d.y === undefined) return d.color ?? "#999";
      const score = Math.max(1, Math.min(5, Math.round(d.y)));
      return props.colors![score]; // 1..5 → カラーマップ
    };
    const pointColor = decidePointColor();

    if (d.y === null || d.y === undefined) {
      return {
        value: null,
        name: useCalendarMonth
          ? d.x
          : fmtMD(endStr || startStr, baselineD ?? undefined),
        start: startStr,
        end: endStr || startStr,
        itemStyle: {
          color: pointColor,
          borderColor: pointColor,
          borderWidth: 0,
        },
        symbolSize: props.pointSize ?? d.size ?? 10,
        condition: d.condition ?? "",
        color: d.color,
      };
    }

    return {
      value: d.y,
      name: useCalendarMonth
        ? d.x
        : fmtMD(endStr || startStr, baselineD ?? undefined),
      start: startStr,
      end: endStr || startStr,
      itemStyle: {
        color: pointColor,
        borderColor: pointColor,
        borderWidth: 0,
      },
      symbolSize: props.pointSize ?? d.size ?? 10,
      condition: d.condition ?? "",
      color: d.color,
    };
  });

  // ラベル表示間隔を決定
  const dataaggregationDays = useCalendarMonth ? 30 : autoChunk;

  const { adjustedXLabels, labelsForXAxis, labeledIndices } =
    getAxisLabelsAndIndices({
      range,
      totalDaysRange,
      xLabels, // 直前で作った xLabels（M/D or "M月"）
      aggregated, // 集計済みデータ
      items, // filterByStartEnd 済みの元データ
      useCalendarMonth,
      dataaggregationDays,
    });

  const { isAverage: tooltipIsAverage } = getPeriodMeta(
    totalDaysRange,
    useCalendarMonth,
    dataaggregationDays
  );

  // labeledIndices を集合化
  const labeledIndexSet = new Set<number>(labeledIndices);

  const option: EChartsOption = {
    backgroundColor: "white", // グラフ全体の背景色
    // ==== グリッド（グラフ描画領域）の余白設定 ===============================================
    grid: {
      ...props.grid,
      containLabel: true, // ラベルを含めて余白を計算するかどうか
    },
    // ==== x軸 ============================================================================
    xAxis: {
      type: "category", // カテゴリ軸
      data: xLabels, // ラベル配列（日付）
      boundaryGap: false,
      axisLabel: {
        fontSize: 12,
        color: "#666",
        interval: 0,
        showMinLabel: true,
        showMaxLabel: true,
        hideOverlap: false,
        formatter: (_value: string, idx: number) => {
          const lbl = labelsForXAxis[idx] ?? "";
          if (!lbl) return "";
          // カレンダー月表示なら既にM月形式なのでそのまま返す
          if (useCalendarMonth) return lbl;
          // xLabelsはM/D 形式になっているのでそのまま返す
          if (lbl.includes("/")) return lbl;

          // それ以外はパースして M/D を返す（保険）
          const lblStart = lbl.split("〜", 1)[0].trim();
          const dStrict = dayjs(
            lblStart,
            ["YYYY-MM-DD", "YYYY/MM/DD", "MM/DD", "M/D"],
            true
          );
          const d = dStrict.isValid() ? dStrict : dayjs(lblStart); // 厳密で失敗したら緩いパースにフォールバック
          return d.isValid() ? d.format("M/D") : lbl;
        },
      }, // 軸ラベルのスタイル
      axisLine: { lineStyle: { color: "#ddd" } }, // 軸線のスタイル
      axisTick: { show: false }, // 軸目盛りを非表示
      splitLine: {
        // labeledIndices に一致させる（1y/6m/1w は compute 側で全点化済み）
        show: true,
        interval: (index: number) => labeledIndexSet.has(index),
        lineStyle: {
          color: "#e0e0e0",
          type: "solid",
          width: 1,
        },
      },
    },
    // ==== y軸 ============================================================================
    yAxis: {
      type: "value", // 数値軸
      min: 1, // 最小値
      max: 5, // 最大値
      interval: 1, // 目盛り間隔
      axisLabel: {
        fontSize: 12, // ラベル文字サイズ
        color: "transparent", // Y軸ラベル色 ※文字は透明にして画像だけ見せる
        // リッチテキストスタイル（画像アイコンを背景として使用）
        margin: 20, // アイコンをグリッド外側へさらに左に寄せる（軸線からの距離）
        rich: {
          veryGood: {
            height: 20,
            width: 20,
            align: "center",
            backgroundColor: {
              image: props.icons?.veryGood,
            },
          },
          good: {
            height: 20,
            width: 20,
            align: "center",
            backgroundColor: {
              image: props.icons?.good,
            },
          },
          normal: {
            height: 20,
            width: 20,
            align: "center",
            backgroundColor: {
              image: props.icons?.normal,
            },
          },
          bad: {
            height: 20,
            width: 20,
            align: "center",
            backgroundColor: {
              image: props.icons?.bad,
            },
          },
          veryBad: {
            height: 20,
            width: 20,
            align: "center",
            backgroundColor: {
              image: props.icons?.veryBad,
            },
          },
          sp: { width: 6, height: 16 },
        },
        formatter(value: number) {
          if (value === 5) return "{veryGood|}{sp|}";
          if (value === 4) return "{good|}{sp|}";
          if (value === 3) return "{normal|}{sp|}";
          if (value === 2) return "{bad|}{sp|}";
          if (value === 1) return "{veryBad|}{sp|}";
          return "";
        },
      },
      axisLine: { lineStyle: { color: "#ddd" } }, // 軸線のスタイル
      axisTick: { show: false }, // 軸目盛りを非表示
      splitLine: {
        show: true,
        lineStyle: {
          color: "#e0e0e0", // 補助線の色
          type: "solid", // 補助線の種類
          width: 1, // 補助線の太さ
        },
      },
    },
    // ==== データ系列 ============================================================================
    series: [
      {
        name: "体調", // 凡例と連動させるため、legend.data名を設定
        type: "line",
        data: seriesData, // データ配列
        connectNulls: true, // 不連続でも実線でつなぐ
        markLine: {
          silent: true,
          symbol: ["none", "none"], // 矢印なし
          lineStyle: { color: "#e0e0e0", width: 1 },
          emphasis: { disabled: true },
          data: labeledIndices.map((i) => ({ xAxis: adjustedXLabels[i] })),
        },
        smooth: false, // 点を直線で結ぶ
        lineStyle: { color: "#999", width: 2 }, // 線のスタイル
        symbol: "circle", // データ点の形状
        showSymbol: true, // データ点を表示
        itemStyle: {
          borderWidth: 0,
        },
        emphasis: { focus: "series" }, // ホバー時の強調設定
      },
    ],
    // ==== ツールチップ ===========================================================================
    tooltip: {
      trigger: "item", // アイテム（点）単位で表示
      triggerOn: "mousemove", // マウスオーバーで表示
      axisPointer: { show: false }, // 軸ポインタは表示しない
      formatter(params: any) {
        const d = params?.data || {};
        const sD = toDayjsSafe(String(d.start));
        const eD = toDayjsSafe(
          String(d.end),
          sD ? { baseline: sD, preferEnd: true } : undefined
        );
        const s = sD ? sD.format("M月D日") : "";
        const e = eD ? eD.format("M月D日") : "";

        if (s && e && s !== e) {
          return tooltipIsAverage ? `${s}～${e}の平均` : `${s}～${e}`;
        }
        return s || e || "";
      },
    },
  };
  return option;
});

// 集計
const aggregated = computed(() => {
  const rawItems = props.bodyConditions || [];
  const filtered = filterByStartEnd(rawItems, props.startDate, props.endDate);

  const dateAxis =
    props.startDate && props.endDate
      ? generateDateAxis(props.startDate, props.endDate)
      : [];

  const items = dateAxis.map((date) => {
    const found = filtered.find((it) => it.dates === date);
    return found ?? { dates: date, chartData: null };
  });

  const { chunkSize, useCalendarMonth } = decideChunkAndLabel(
    props.range,
    items
  );

  return useCalendarMonth
    ? aggregateByCalendarMonth(items)
    : aggregate(items, chunkSize);
});

// 総日数
const totalDaysRange = computed(() => {
  if (props.range && standardDaysMap[props.range]) {
    return standardDaysMap[props.range];
  }
  if (props.startDate && props.endDate) {
    return dayjs(props.endDate).diff(dayjs(props.startDate), "day") + 1;
  }
  return aggregated.value.length;
});

// 直近xx日平均のタイトル文言を生成
const explainTitle = computed(() =>
  makeExplainTitleFromAggregated(aggregated.value, props.labels!)
);

// 注釈
const aggregationNote = computed(() => {
  const total = totalDaysRange.value;
  if (total >= 91) return "※1カ月ごとの平均";
  if (total >= 32) return "※15日ごとの平均";
  return "";
});

// 凡例
const legendItems = computed(() => [
  { label: "とてもいい", color: props.icons?.veryGood },
  { label: "いい", color: props.icons?.good },
  { label: "ふつう", color: props.icons?.normal },
  { label: "わるい", color: props.icons?.bad },
  { label: "とてもわるい", color: props.icons?.veryBad },
]);

// grid左端にあわせる
const onChartReady = (chart) => {
  const gridRect = chart
    .getModel()
    .getComponent("grid")
    .coordinateSystem.getRect();

  noteOffset.value = gridRect.x;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "chart-title-row",
      style: _normalizeStyle({
        '--note-offset': props.titleCond.show ? '20px' : noteOffset.value + 'px',
      })
    }, [
      _withDirectives(_createElementVNode("div", _hoisted_2, "体調", 512), [
        [_vShow, props.titleCond.show]
      ]),
      _createElementVNode("div", _hoisted_3, _toDisplayString(aggregationNote.value), 1),
      _createElementVNode("div", _hoisted_4, _toDisplayString(explainTitle.value), 1)
    ], 4),
    _createVNode(Chart, {
      option: conditionLineOption.value,
      class: _normalizeClass([_ctx.$attrs.class, "condition-line-chart"]),
      style: { height: '100%', width: '100%' },
      onChartReady: onChartReady
    }, null, 8, ["option", "class"]),
    _createElementVNode("ul", _hoisted_5, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(legendItems.value, (item) => {
        return (_openBlock(), _createElementBlock("li", {
          key: item.label
        }, [
          _createElementVNode("span", {
            class: "legend-color",
            style: _normalizeStyle({ backgroundImage: `url(${item.color})` })
          }, null, 4),
          _createElementVNode("span", _hoisted_6, _toDisplayString(item.label), 1),
          _createElementVNode("span", _hoisted_7, _toDisplayString(item.value), 1)
        ]))
      }), 128))
    ])
  ]))
}
}

})